import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '../api/api-context.js';
import type { ApiClient } from '../api/client.js';
import {
  confirmUpload,
  createUploadTickets,
  putToStorage,
  StorageUploadError,
  type UploadDeclaration,
} from './media-api.js';
import type { Media, UploadTicket } from './media-contract.js';
import {
  browserImageCodec,
  prepareImage,
  type WatermarkSpec,
  type ImageCodec,
  type PreparedImage,
} from './image-prep.js';

/**
 * The upload state machine.
 *
 * A photo's journey is prepare → ticket → PUT thumb → PUT full → confirm, and on a market-street
 * 4G connection any of those steps can simply stop happening. So each file carries its own state
 * and its own error, the whole thing is resumable per file, and `retry` starts from the ticket
 * rather than the beginning: the compressed bytes are kept in memory, so recovering from a
 * dropped connection re-sends 300KB and does not make anyone photograph the garment again.
 *
 * Files are uploaded ONE AT A TIME on purpose. Six parallel uploads on a congested link finish
 * later than six sequential ones, and they make every progress bar crawl at once, which reads as
 * "nothing is happening" to the person holding the phone.
 */

export type UploadStatus = 'preparing' | 'queued' | 'uploading' | 'confirming' | 'done' | 'failed';

export interface UploadItem {
  clientId: string;
  fileName: string;
  /** Bytes of the untouched camera original — what the shop was spared from uploading. */
  originalBytes: number;
  /** Bytes actually sent, once prepared. */
  preparedBytes: number | undefined;
  status: UploadStatus;
  /** 0–1. Drives the per-file progress bar. */
  progress: number;
  attempts: number;
  /** A translation key, so the component decides the wording. */
  errorKey: string | undefined;
  media: Media | undefined;
  /** Local object URL of the prepared thumbnail, shown before the server knows about it. */
  previewUrl: string | undefined;
}

export interface UseMediaUploadsOptions {
  entityType: string;
  entityId?: string | null;
  kind?: string;
  visibility?: 'internal' | 'customer_visible';
  /** Threaded into the ticket request only when set — see `CreateTicketsInput`. */
  folderId?: string | null;
  onUploaded?: (media: Media) => void;
  /**
   * The shop's watermark, when it has one switched on. Burned into the full-size image in the
   * same canvas pass that resizes it and strips the EXIF — see `image-prep.ts`. Passed in by
   * the caller rather than read here, because a watermark belongs on the photographs a shop
   * SHOWS and not on, say, an expense receipt somebody snapped for the books.
   */
  watermark?: WatermarkSpec | undefined;
  /** Test seams. */
  codec?: ImageCodec;
  put?: typeof putToStorage;
  client?: ApiClient;
}

export interface UseMediaUploads {
  items: UploadItem[];
  addFiles: (files: readonly File[]) => Promise<void>;
  retry: (clientId: string) => Promise<void>;
  dismiss: (clientId: string) => void;
  busy: boolean;
}

/** The full image is ~25× the thumbnail, so it owns almost all of the progress bar. */
const THUMB_PROGRESS_SHARE = 0.05;

let sequence = 0;
function nextClientId(): string {
  sequence += 1;
  return `upload-${Date.now().toString(36)}-${sequence}`;
}

function errorKeyFor(error: unknown): string {
  if (error instanceof StorageUploadError) {
    return error.retryable || error.status === undefined
      ? 'media.error.connectionLost'
      : 'media.error.uploadRefused';
  }
  return 'media.error.uploadFailed';
}

export function useMediaUploads(options: UseMediaUploadsOptions): UseMediaUploads {
  const contextClient = useApiClient();
  const client = options.client ?? contextClient;
  const [items, setItems] = useState<UploadItem[]>([]);
  const [busy, setBusy] = useState(false);

  /** Prepared bytes live outside React state — they are large, and never rendered. */
  const preparedRef = useRef(new Map<string, PreparedImage>());
  const objectUrlsRef = useRef<string[]>([]);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(
    () => () => {
      for (const url of objectUrlsRef.current) revokeObjectUrl(url);
      objectUrlsRef.current = [];
      preparedRef.current.clear();
    },
    [],
  );

  const patch = useCallback((clientId: string, changes: Partial<UploadItem>) => {
    setItems((current) =>
      current.map((item) => (item.clientId === clientId ? { ...item, ...changes } : item)),
    );
  }, []);

  /** PUT both variants, then confirm. Shared by the first attempt and every retry. */
  const sendTicket = useCallback(
    async (clientId: string, ticket: UploadTicket, prepared: PreparedImage) => {
      const put = optionsRef.current.put ?? putToStorage;

      patch(clientId, { status: 'uploading', progress: 0, errorKey: undefined });

      await put({
        url: ticket.upload.thumb.url,
        body: prepared.thumb.blob,
        headers: ticket.upload.thumb.headers,
        onProgress: (fraction) => patch(clientId, { progress: fraction * THUMB_PROGRESS_SHARE }),
      });

      await put({
        url: ticket.upload.full.url,
        body: prepared.full.blob,
        headers: ticket.upload.full.headers,
        onProgress: (fraction) =>
          patch(clientId, {
            progress: THUMB_PROGRESS_SHARE + fraction * (1 - THUMB_PROGRESS_SHARE),
          }),
      });

      patch(clientId, { status: 'confirming', progress: 1 });
      const media = await confirmUpload(client, ticket.mediaId);

      patch(clientId, { status: 'done', progress: 1, media, errorKey: undefined });
      // The bytes are safely in the bucket; stop holding several megabytes of them.
      preparedRef.current.delete(clientId);
      optionsRef.current.onUploaded?.(media);
    },
    [client, patch],
  );

  const declarationFor = (clientId: string, prepared: PreparedImage): UploadDeclaration => ({
    clientId,
    originalFilename: prepared.originalFilename,
    originalBytes: prepared.originalBytes,
    full: {
      bytes: prepared.full.blob.size,
      contentType: prepared.contentType,
      width: prepared.full.width,
      height: prepared.full.height,
    },
    thumb: {
      bytes: prepared.thumb.blob.size,
      contentType: prepared.contentType,
      width: prepared.thumb.width,
      height: prepared.thumb.height,
    },
  });

  const ticketInput = (files: UploadDeclaration[]) => {
    const current = optionsRef.current;
    return {
      entityType: current.entityType,
      ...(current.entityId === undefined ? {} : { entityId: current.entityId }),
      ...(current.kind ? { kind: current.kind } : {}),
      ...(current.visibility ? { visibility: current.visibility } : {}),
      ...(current.folderId !== undefined ? { folderId: current.folderId } : {}),
      files,
    };
  };

  const addFiles = useCallback(
    async (files: readonly File[]) => {
      if (files.length === 0) return;
      setBusy(true);

      const staged = files.map((file) => ({ clientId: nextClientId(), file }));
      setItems((current) => [
        ...current,
        ...staged.map(({ clientId, file }) => ({
          clientId,
          fileName: file.name,
          originalBytes: file.size,
          preparedBytes: undefined,
          status: 'preparing' as UploadStatus,
          progress: 0,
          attempts: 0,
          errorKey: undefined,
          media: undefined,
          previewUrl: undefined,
        })),
      ]);

      // 1. Compress everything first. Nothing has touched the network yet.
      const ready: { clientId: string; declaration: UploadDeclaration }[] = [];
      for (const { clientId, file } of staged) {
        try {
          const codec = optionsRef.current.codec ?? browserImageCodec;
          const prepared = await prepareImage(file, codec, {
            ...(optionsRef.current.watermark ? { watermark: optionsRef.current.watermark } : {}),
          });
          preparedRef.current.set(clientId, prepared);

          const previewUrl = createObjectUrl(prepared.thumb.blob);
          if (previewUrl) objectUrlsRef.current.push(previewUrl);

          patch(clientId, {
            status: 'queued',
            preparedBytes: prepared.full.blob.size,
            ...(previewUrl ? { previewUrl } : {}),
          });
          ready.push({ clientId, declaration: declarationFor(clientId, prepared) });
        } catch {
          patch(clientId, { status: 'failed', errorKey: 'media.error.prepareFailed' });
        }
      }

      // 2. One round trip for the whole batch — six presign requests over 4G is five too many.
      if (ready.length > 0) {
        try {
          const tickets = await createUploadTickets(
            client,
            ticketInput(ready.map((entry) => entry.declaration)),
          );

          for (const ticket of tickets) {
            const prepared = preparedRef.current.get(ticket.clientId);
            if (!prepared) continue;
            patch(ticket.clientId, { attempts: 1 });
            try {
              await sendTicket(ticket.clientId, ticket, prepared);
            } catch (error) {
              patch(ticket.clientId, { status: 'failed', errorKey: errorKeyFor(error) });
            }
          }
        } catch (error) {
          for (const entry of ready) {
            patch(entry.clientId, { status: 'failed', errorKey: errorKeyFor(error) });
          }
        }
      }

      setBusy(false);
    },
    [client, patch, sendTicket],
  );

  /**
   * Try again after a failure. A fresh ticket is requested rather than the old URL reused,
   * because a presigned URL that was issued five minutes ago has expired — which is exactly the
   * situation after a connection drop that the person waited out.
   */
  const retry = useCallback(
    async (clientId: string) => {
      const prepared = preparedRef.current.get(clientId);
      if (!prepared) {
        patch(clientId, { status: 'failed', errorKey: 'media.error.retryUnavailable' });
        return;
      }

      setBusy(true);
      setItems((current) =>
        current.map((item) =>
          item.clientId === clientId
            ? { ...item, status: 'queued', errorKey: undefined, attempts: item.attempts + 1 }
            : item,
        ),
      );

      try {
        const [ticket] = await createUploadTickets(
          client,
          ticketInput([declarationFor(clientId, prepared)]),
        );
        if (!ticket) throw new Error('no ticket returned');
        await sendTicket(clientId, ticket, prepared);
      } catch (error) {
        patch(clientId, { status: 'failed', errorKey: errorKeyFor(error) });
      } finally {
        setBusy(false);
      }
    },
    [client, patch, sendTicket],
  );

  const dismiss = useCallback((clientId: string) => {
    preparedRef.current.delete(clientId);
    setItems((current) => {
      const target = current.find((item) => item.clientId === clientId);
      if (target?.previewUrl) revokeObjectUrl(target.previewUrl);
      return current.filter((item) => item.clientId !== clientId);
    });
  }, []);

  return { items, addFiles, retry, dismiss, busy };
}

/** jsdom has no object URLs, and neither do very old browsers; the preview is optional. */
function createObjectUrl(blob: Blob): string | undefined {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return undefined;
  try {
    return URL.createObjectURL(blob);
  } catch {
    return undefined;
  }
}

function revokeObjectUrl(url: string): void {
  if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
}
